import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Heart, 
  MessageCircle, 
  Bookmark, 
  Eye, 
  Calendar, 
  MapPin, 
  Palette, 
  DollarSign, 
  Ruler,
  Play,
  Volume2,
  FileText,
  Star,
  ShoppingCart,
  ExternalLink,
  Share2,
  Maximize2,
  Minimize2,
  Megaphone,
  QrCode,
  Camera,
  Music,
  Video,
  Users,
  MessageSquare,
  Phone,
  Mail,
  Globe,
  Navigation,
  Clock,
  Tag,
  Award,
  Smartphone,
  Download,
  X,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Move
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { fetchArtworkDetailsWithExtras } from '../utils/api';
import SEO from './SEO';
import { getSEOConfig } from '../utils/seoConfig';
import AppDownloadBanner from './AppDownloadBanner';
import '../styles/ArtworkDetailPage.css';

const ArtworkDetailPage = () => {
  const { vcId } = useParams();
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fit, setFit] = useState(true);
  const [gaveLove, setGaveLove] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showAppPromo, setShowAppPromo] = useState(false); // Disabled ad popup
  
  // Lightbox state
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPan, setLastPan] = useState({ x: 0, y: 0 });
  
  // Video player state
  const [playingVideo, setPlayingVideo] = useState(null);
  const videoRef = useRef(null);
  
  // Determine if we're in admin or artist context based on the current path
  const isAdminContext = window.location.pathname.includes('/admin/');
  const backPath = isAdminContext ? '/admin/artworks' : '/artist/artworks';

  // Debug logging
  console.log('ArtworkDetailPage: Component rendering');
  console.log('ArtworkDetailPage: vcId =', vcId);
  console.log('ArtworkDetailPage: loading =', loading);
  console.log('ArtworkDetailPage: artwork =', artwork);
  console.log('ArtworkDetailPage: error =', error);

  useEffect(() => {
    const loadArtwork = async () => {
      try {
        console.log('ArtworkDetailPage: Starting to load artwork...');
        setLoading(true);
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        console.log('ArtworkDetailPage: Current user =', user);
        setCurrentUser(user);
        
        // Call the new API function
        console.log('ArtworkDetailPage: Calling fetchArtworkDetailsWithExtras...');
        const data = await fetchArtworkDetailsWithExtras(
          parseInt(vcId),
          user?.id || '00000000-0000-0000-0000-000000000000'
        );
        console.log('ArtworkDetailPage: API response =', data);

        setArtwork(data);
        setGaveLove(data.user_liked || false);
        
        // Track view activity
        if (user) {
          await supabase
            .from('user_activity')
            .insert({
              vc_id: parseInt(vcId),
              activity_type: 'view',
              uuid: user.id
            });
        }
        
      } catch (err) {
        console.error('ArtworkDetailPage: Error loading artwork:', err);
        setError(err.message);
      } finally {
        console.log('ArtworkDetailPage: Setting loading to false');
        setLoading(false);
      }
    };

    if (vcId) {
      loadArtwork();
    }
  }, [vcId]);

  const handleLike = async () => {
    if (!currentUser) {
      // Show app download promotion instead of login redirect
      setShowAppPromo(true);
      return;
    }

    try {
      if (!gaveLove) {
        await supabase
          .from('user_activity')
          .insert({
            activity_type: 'liked_artwork',
            vc_id: parseInt(vcId),
            uuid: currentUser.id
          });
        
        setGaveLove(true);
        setArtwork(prev => ({
          ...prev,
          total_likes: (prev.total_likes || 0) + 1
        }));
      }
    } catch (error) {
      console.error('Error liking artwork:', error);
    }
  };

  const handleBookmark = async () => {
    if (!currentUser) {
      setShowAppPromo(true);
      return;
    }

    try {
      if (!artwork.user_bookmarked) {
        await supabase
          .from('user_bookmarks')
          .insert({
            vc_id: parseInt(vcId),
            uuid: currentUser.id
          });
        
        setArtwork(prev => ({
          ...prev,
          user_bookmarked: true
        }));
      } else {
        await supabase
          .from('user_bookmarks')
          .delete()
          .eq('vc_id', parseInt(vcId))
          .eq('uuid', currentUser.id);
        
        setArtwork(prev => ({
          ...prev,
          user_bookmarked: false
        }));
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const handleShare = async () => {
    try {
      const shareUrl = `https://share.vibrantcast.com/feelArtStory?id=${vcId}`;
      await navigator.clipboard.writeText(shareUrl);
      
      // Show success message
      alert('Link copied to clipboard!');
      
      // Track share activity
      if (currentUser) {
        await supabase
          .from('user_activity')
          .insert({
            activity_type: 'artwork_shared',
            vc_id: parseInt(vcId),
            uuid: currentUser.id
          });
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handlePulse = () => {
    if (!currentUser) {
      setShowAppPromo(true);
      return;
    }
    // Pulse functionality would be implemented here
    alert('Pulse feature - share your inspiration!');
  };

  const handleInquiry = () => {
    if (!currentUser) {
      setShowAppPromo(true);
      return;
    }
    // Inquiry functionality would be implemented here
    alert('Send inquiry to artist');
  };

  // Lightbox functions
  const openLightbox = (imageSrc) => {
    setLightboxImage(imageSrc);
    setShowLightbox(true);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    setLightboxImage(null);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.5, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.5, 0.5));
  };

  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsPanning(true);
      setLastPan({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning && zoom > 1) {
      const deltaX = e.clientX - lastPan.x;
      const deltaY = e.clientY - lastPan.y;
      setPan(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      setLastPan({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  // Video player functions
  const playVideo = (videoSrc) => {
    setPlayingVideo(videoSrc);
  };

  const closeVideo = () => {
    setPlayingVideo(null);
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDimensions = (width, height, depth) => {
    if (!width && !height) return 'N/A';
    let dims = `${width || 0}" × ${height || 0}"`;
    if (depth) dims += ` × ${depth}"`;
    return dims;
  };

  if (loading) {
    return (
      <div className="artwork-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading artwork details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="artwork-detail-error">
        <h2>Error loading artwork</h2>
        <p>{error}</p>
        <button onClick={() => navigate(backPath)} className="back-btn">
          <ArrowLeft size={16} />
          Back to Artworks
        </button>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="artwork-detail-not-found">
        <h2>Artwork not found</h2>
        <button onClick={() => navigate(backPath)} className="back-btn">
          <ArrowLeft size={16} />
          Back to Artworks
        </button>
      </div>
    );
  }

  return (
    <div className="artwork-detail-page">
      {/* App Download Banner */}
      {showAppPromo && (
        <AppDownloadBanner />
      )}

      <SEO {...getSEOConfig.artwork({
        title: artwork?.title,
        artist_name: artwork?.artist_nickname,
        description: artwork?.description,
        image_url: artwork?.cover_image,
        medium: artwork?.attributes?.find(attr => attr.type === 'medium')?.title,
        style: artwork?.attributes?.find(attr => attr.type === 'style')?.title
      })} />
      
      {/* Main Content */}
      <div className="artwork-main-container">
        {/* Full-screen Image Section */}
        <div className="artwork-image-section">
          <div className="artwork-main-image-container">
            <img 
              src={artwork.cover_image} 
              alt={artwork.title}
              className={`artwork-main-image ${fit ? 'fit-contain' : 'fit-cover'}`}
              onError={(e) => {
                e.target.src = '/images/placeholder-artwork.jpg';
              }}
              onClick={() => openLightbox(artwork.cover_image)}
              style={{ cursor: 'pointer' }}
            />
            
            {/* VibrantCast Logo Overlay */}
            <div className="logo-overlay">
              <img 
                src="/images/logo-120.png" 
                alt="VibrantCast" 
                className="overlay-logo"
              />
            </div>

            {/* QR Code for Physical Discovery */}
            <div className="qr-code-overlay">
              <QrCode size={24} />
              <span>Scan to discover in app</span>
            </div>
          </div>
        </div>

        {/* Image Controls */}
        <div className="image-controls">
          <div className="fit-controls">
            <button 
              className={`fit-btn ${fit ? 'active' : ''}`}
              onClick={() => setFit(true)}
              title="Fit to screen"
            >
              <Minimize2 size={20} color={fit ? "#ffffff" : "#6c757d"} />
            </button>
            <button 
              className={`fit-btn ${!fit ? 'active' : ''}`}
              onClick={() => setFit(false)}
              title="Fill screen"
            >
              <Maximize2 size={20} color={!fit ? "#ffffff" : "#6c757d"} />
            </button>
          </div>
          
          <div className="action-controls">
            <button 
              className={`action-btn like-btn ${gaveLove ? 'liked' : ''}`}
              onClick={handleLike}
            >
              <Heart size={20} color={gaveLove ? "#ffffff" : "#6c757d"} fill={gaveLove ? "#ffffff" : "none"} />
            </button>
            
            <button className="action-btn pulse-btn" onClick={handlePulse}>
              <Megaphone size={20} color="#6c757d" />
              <span>pulse</span>
            </button>
            
            <button 
              className={`action-btn bookmark-btn ${artwork.user_bookmarked ? 'bookmarked' : ''}`}
              onClick={handleBookmark}
            >
              <Bookmark size={20} color={artwork.user_bookmarked ? "#ffffff" : "#6c757d"} fill={artwork.user_bookmarked ? "#ffffff" : "none"} />
            </button>
          </div>
        </div>

        {/* Artwork Information */}
        <div className="artwork-info-section">
          {/* Artist Name */}
          <div className="artist-name-section">
            <h3 className="artist-name">{artwork.artist_nickname}</h3>
          </div>

          {/* Artwork Title */}
          <div className="artwork-title-section">
            <h1 className="artwork-title">{artwork.title}</h1>
          </div>

          {/* Dimensions */}
          <div className="dimensions-section">
            <span className="dimension">height: {artwork.height || 0}"</span>
            <span className="dimension">width: {artwork.width || 0}"</span>
            {artwork.depth && <span className="dimension">depth: {artwork.depth}"</span>}
          </div>

          {/* Price and Status */}
          <div className="price-status-section">
            {artwork.price && artwork.price > 0 && artwork.status !== 'Not For Sale' && (
              <div className="price-display">
                <h2 className="price">{formatPrice(artwork.price)}</h2>
              </div>
            )}
            
            {artwork.status === 'Sold' && (
              <div className="sold-status">
                <span className="sold-text">SOLD - Original Not Available</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="purchase-actions">
            {artwork.status === 'For Sale' && (
              <button className="purchase-btn" onClick={handleInquiry}>
                <MessageSquare size={16} color="#ffffff" />
                Purchase Inquiry
              </button>
            )}
            
            {artwork.has_available_prints && (
              <a 
                href={artwork.payment_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="prints-btn"
              >
                <ShoppingCart size={16} color="#6f42c1" />
                Prints
              </a>
            )}
            
            {artwork.isAuction && (
              <button className="bid-btn">
                <Award size={16} color="#ffffff" />
                Place Bid
              </button>
            )}
          </div>

          {/* Enhanced Media Section */}
          {artwork.media && artwork.media.length > 0 && (
            <div className="media-section">
              <h3 className="section-title">
                <Camera size={20} color="#212529" />
                Additional Media
              </h3>
              <div className="media-grid">
                {artwork.media.map((media, index) => (
                  <div key={index} className="media-item">
                    {media.type === 'Video' ? (
                      <div className="video-thumbnail" onClick={() => playVideo(media.source)}>
                        <Video size={24} />
                        <span>{media.title || 'Video'}</span>
                        <button className="play-video-btn">
                          <Play size={32} />
                        </button>
                      </div>
                    ) : (
                      <img 
                        src={media.source} 
                        alt={media.title || `Media ${index + 1}`}
                        onError={(e) => {
                          e.target.src = '/images/placeholder-artwork.jpg';
                        }}
                        onClick={() => openLightbox(media.source)}
                        style={{ cursor: 'pointer' }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audio Clips Section */}
          {artwork.audio_clips && artwork.audio_clips.length > 0 && (
            <div className="audio-section">
              <h3 className="section-title">
                <Music size={20} color="#212529" />
                Listen to Audio Story
              </h3>
              <p className="audio-description">
                {artwork.more?.inspiration || 'Audio story available for this artwork.'}
              </p>
              <div className="audio-clips-grid">
                {artwork.audio_clips.map((audio, index) => (
                  <div key={index} className="audio-clip-item">
                    {audio.source ? (
                      <div className="audio-player-container">
                        <audio 
                          controls 
                          src={audio.source} 
                          className="audio-player"
                          preload="metadata"
                        >
                          Your browser does not support the audio element.
                        </audio>
                        <div className="audio-info">
                          <span className="audio-title">{audio.title || `Audio ${index + 1}`}</span>
                          {audio.duration && (
                            <span className="audio-duration">{audio.duration}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="audio-player-placeholder">
                        <Play size={40} />
                        <span>Audio {index + 1}</span>
                        <small>Audio file not available</small>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fallback Audio Section for audio_story field */}
          {!artwork.audio_clips && artwork.audio_story && (
            <div className="audio-section">
              <h3 className="section-title">
                <Music size={20} color="#212529" />
                Listen to Audio Story
              </h3>
              <p className="audio-description">
                {artwork.more?.inspiration || 'Audio story available for this artwork.'}
              </p>
              <div className="audio-player-container">
                <audio 
                  controls 
                  src={artwork.audio_story} 
                  className="audio-player"
                  preload="metadata"
                >
                  Your browser does not support the audio element.
                </audio>
                <div className="audio-info">
                  <span className="audio-title">Audio Story</span>
                </div>
              </div>
            </div>
          )}

          {/* Artwork Inspiration */}
          {artwork.more?.inspiration && (
            <div className="inspiration-section">
              <h3 className="section-title">
                <Star size={20} color="#212529" />
                Artwork Inspiration
              </h3>
              <p className="inspiration-text">{artwork.more.inspiration}</p>
            </div>
          )}

          {/* Creation Process */}
          {artwork.more?.process && (
            <div className="process-section">
              <h3 className="section-title">
                <Palette size={20} color="#212529" />
                Creation Process
              </h3>
              <p className="process-text">{artwork.more.process}</p>
            </div>
          )}

          {/* Artwork Description */}
          {artwork.description && (
            <div className="description-section">
              <h3 className="section-title">
                <FileText size={20} color="#212529" />
                About the Artwork
              </h3>
              <p className="description-text">{artwork.description}</p>
            </div>
          )}

          {/* Artist Profile Section */}
          <div className="artist-profile-section">
            <div className="artist-profile-header">
              <h3 className="artist-profile-title">
                <Users size={20} color="#212529" />
                Artist {artwork.artist_nickname}
              </h3>
              
              <button className="follow-btn">
                {artwork.user_follows_artist ? 'Following' : 'Follow'}
              </button>
            </div>
            
            <div className="artist-profile-content">
              <div className="artist-avatar">
                <img 
                  src={artwork.artist_profile_img || '/images/placeholder-avatar.jpg'} 
                  alt={artwork.artist_nickname}
                  onError={(e) => {
                    e.target.src = '/images/placeholder-avatar.jpg';
                  }}
                />
              </div>
              
              {artwork.artist_bio && (
                <div className="artist-bio">
                  <p>{artwork.artist_bio}</p>
                </div>
              )}
            </div>

            {/* Artist Contact & Social */}
            <div className="artist-contact-section">
              <h4>Connect with Artist</h4>
              <div className="contact-buttons">
                <button className="contact-btn">
                  <MessageSquare size={16} color="#6c757d" />
                  Message
                </button>
                <button className="contact-btn">
                  <Phone size={16} color="#6c757d" />
                  Call
                </button>
                <button className="contact-btn">
                  <Mail size={16} color="#6c757d" />
                  Email
                </button>
                <button className="contact-btn">
                  <Globe size={16} color="#6c757d" />
                  Website
                </button>
              </div>
            </div>
          </div>

          {/* More from Artist Section */}
          {artwork.more_from_artist && artwork.more_from_artist.length > 0 && (
            <div className="more-from-artist-section">
              <h3 className="section-title">
                <Star size={20} color="#212529" />
                Other works by {artwork.artist_nickname}
              </h3>
              
              <div className="artist-works-grid">
                {artwork.more_from_artist.map((relatedArtwork) => (
                  <div 
                    key={relatedArtwork.vc_id} 
                    className="related-artwork-card"
                    onClick={() => navigate(`/artwork/${relatedArtwork.vc_id}`)}
                  >
                    <img 
                      src={relatedArtwork.cover_image} 
                      alt={relatedArtwork.title}
                      onError={(e) => {
                        e.target.src = '/images/placeholder-artwork.jpg';
                      }}
                    />
                    <div className="related-artwork-info">
                      <h4 className="related-title">{relatedArtwork.title}</h4>
                      <p className="related-price">{formatPrice(relatedArtwork.price)}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <button 
                className="view-all-works-btn"
                onClick={() => navigate(`/artist/${artwork.artist_handle}`)}
              >
                View all works by {artwork.artist_nickname}
                <ArrowLeft size={16} className="arrow-icon" />
              </button>
            </div>
          )}

          {/* App Download Promotion Section */}
          <div className="app-promo-section">
            <div className="app-promo-content">
              <div className="app-promo-icon">
                <Smartphone size={48} />
              </div>
              <div className="app-promo-text">
                <h3>Get the Full VibrantCast Experience</h3>
                <p>Discover more art, connect with artists, and explore exhibitions near you</p>
                <div className="app-features">
                  <span className="feature-item">
                    <MapPin size={16} />
                    Local Art Discovery
                  </span>
                  <span className="feature-item">
                    <Heart size={16} />
                    Artist Connections
                  </span>
                  <span className="feature-item">
                    <Users size={16} />
                    Community Events
                  </span>
                </div>
              </div>
              <div className="app-promo-actions">
                <a 
                  href="https://apps.apple.com/us/app/vibrantcast/id6569221167" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="app-store-btn-large apple-store"
                >
                  <Download size={20} color="#ffffff" />
                  App Store
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.mycompany.vibrantcast" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="app-store-btn-large google-play"
                >
                  <Download size={20} color="#ffffff" />
                  Google Play
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Navigation Bar */}
      <div className="top-navigation">
        <div className="nav-content">
          <button 
            onClick={() => navigate(backPath)} 
            className="back-btn"
          >
            <ArrowLeft size={24} color="#ffffff" />
          </button>
          
          <div className="nav-actions">
            {currentUser && currentUser.id === artwork.artist_uuid && (
              <button className="edit-btn">
                Edit
              </button>
            )}
            
            <button className="share-btn" onClick={handleShare}>
              <Share2 size={20} color="#6c757d" />
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {showLightbox && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <div className="lightbox-header">
              <div className="lightbox-controls">
                <button onClick={handleZoomOut} className="lightbox-btn" title="Zoom Out">
                  <ZoomOut size={20} />
                </button>
                <button onClick={handleZoomIn} className="lightbox-btn" title="Zoom In">
                  <ZoomIn size={20} />
                </button>
                <button onClick={handleReset} className="lightbox-btn" title="Reset">
                  <RotateCcw size={20} />
                </button>
                <span className="zoom-level">{Math.round(zoom * 100)}%</span>
              </div>
              <button onClick={closeLightbox} className="lightbox-close">
                <X size={24} />
              </button>
            </div>
            <div 
              className="lightbox-image-container"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onWheel={handleWheel}
              style={{ cursor: isPanning ? 'grabbing' : zoom > 1 ? 'grab' : 'default' }}
            >
              <img 
                src={lightboxImage} 
                alt="Artwork detail"
                className="lightbox-image"
                style={{
                  transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                  transition: isPanning ? 'none' : 'transform 0.1s ease-out'
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {playingVideo && (
        <div className="video-modal-overlay" onClick={closeVideo}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3>Video Player</h3>
              <button onClick={closeVideo} className="video-modal-close">
                <X size={24} />
              </button>
            </div>
            <div className="video-player-container">
              <video 
                ref={videoRef}
                controls 
                autoPlay 
                className="video-player"
                src={playingVideo}
              >
                Your browser does not support the video element.
              </video>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtworkDetailPage;
